// ─────────────────────────────────────────────────────────────────────────────────────────────
// C-b (decision 77): the popup's summary greeting. One or two short FACTS under "Hi, I'm Aidey",
// read from data this person already sees on the screens, never a prompt and never a guess:
//
//   overdue tasks    the Tasks screen's own rule (utils/taskTime isTaskOverdue, their timezone)
//   meetings left    meeting tasks still to come today (not started yet), the Dashboard's
//                    "meetings coming up" rule (routes/pulse.js: same types, start from now on)
//   overdue invoices the Invoices screen's list (InvoicesContext, /api/invoices, which the server
//                    scopes with financialReadScope: the business ledger for the owner and finance,
//                    their own invoices for everyone else) and its overdue rule
//                    (utils/invoiceOverdue). Only in a Business workspace, and only once the list
//                    actually loaded: a failed load says nothing rather than "none overdue".
//
// Nothing notable: no facts at all (an empty list), not an "all clear".
// ─────────────────────────────────────────────────────────────────────────────────────────────
import { isTaskOverdue } from './taskTime';
import { isInvoiceOverdue, localToday } from './invoiceOverdue';
import { toLocalDateStr } from './calendarUtils';

const MEETING = /^(meeting|call|video call)$/i;   // routes/pulse.js MEETING_TYPES

// A meeting still to come today: it starts today and has not started yet.
function meetingLeftToday(t, today, now) {
  if (!t.datetime) return false;
  const d = new Date(t.datetime);
  return !isNaN(d.getTime()) && toLocalDateStr(d) === today && d.getTime() >= now.getTime();
}

const plural = (n, one, many) => `${n} ${n === 1 ? one : many}`;

// tasks: the app's task list. invoices: InvoicesContext's list. invoicesUsable: business workspace
// AND the list loaded. Returns at most two facts: [{ key, text, view }].
export function popupSummaryFacts({ tasks = [], invoices = [], invoicesUsable = false, timezone, now = new Date(), max = 2 }) {
  const today = localToday(now);
  const facts = [];
  const overdueInvoices = invoicesUsable ? (invoices || []).filter((inv) => isInvoiceOverdue(inv, today)).length : 0;
  if (overdueInvoices > 0) {
    facts.push({ key: 'invoices', view: 'Invoices', text: `${plural(overdueInvoices, 'invoice is', 'invoices are')} overdue.` });
  }
  const live = (tasks || []).filter((t) => t && !t.completed);
  const meetings = live.filter((t) => MEETING.test(t.taskType || '') && meetingLeftToday(t, today, now)).length;
  if (meetings > 0) {
    facts.push({ key: 'meetings', view: 'Calendar', text: `${plural(meetings, 'meeting', 'meetings')} still to come today.` });
  }
  const overdueTasks = live.filter((t) => isTaskOverdue(t, timezone)).length;
  if (overdueTasks > 0) {
    facts.push({ key: 'tasks', view: 'Tasks', text: `${plural(overdueTasks, 'task is', 'tasks are')} overdue.` });
  }
  return facts.slice(0, max);
}
